import React from 'react';

import { FeatureFlags } from '~src/ts/FeatureFlags';

import { Formatters, Messages } from '../../../codegen/Localize';
import { TooltipDraftIndicator } from '../../../drafts/components/TooltipDraftIndicator';
import { getFlowDraftId } from '../../../drafts/utils/DraftUtils';
import { editFlow } from '../../../flow/actions/EditFlowReduxAction';
import { Flows } from '../../../flow/resources/Flows';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { FlowDraftItemInfo } from '../../../ts/models/FlowDraftInfo';
import { FlowBaseInfo } from '../../../ts/models/FlowInfo';
import { ContentDraftStatus } from '../../../ts/ServerApi/types/ContentDraft';
import { getAppStore } from '../../AppStore';
import { DetailsTooltipStyles } from '../CardView/DetailsTooltipStyles';
import { MarkupField } from '../Components/MarkupField';
import { BasicField } from './Elements/BasicField';
import { LocationField } from './Elements/LocationField';
import { Name } from './Elements/Name';
import { RelatedEntity } from './Elements/RelatedEntity';
import { Tags } from './Elements/Tags';
import { Timestamp } from './Elements/Timestamp';
import { Tooltip } from './Elements/Tooltip';
import { User } from './Elements/User';
import { ContentTooltipProps, getPersonalSpaceLink,getProjectLink, TooltipDescriptionTestId } from './TooltipHelper';

export function FlowTooltip(props: ContentTooltipProps): JSX.Element {
  const { content, urls } = props;
  const flowInfo = content as FlowBaseInfo;
  const [draftLastEdited, setDraftLastEdited] = React.useState('');
  const [hideEditingLink, setHideEditingLink] = React.useState(false);
  React.useEffect(() => {
    const draftId = getFlowDraftId(flowInfo);
    if (flowInfo.draftStatus !== ContentDraftStatus.none && draftId) {
      Flows.getDraft(draftId.toString()).then((result) => {
        const lastUpdatedAt = (result as unknown as FlowDraftItemInfo).lastUpdatedAt;
        const lastUpdatedAtFormatted: string = Formatters.formatLongBestFitRelativeTime(new Date(lastUpdatedAt));
        setDraftLastEdited(Messages.draftLastEditIndicatorText({LAST_EDITED: lastUpdatedAtFormatted}));

        if (flowInfo.draftStatus === ContentDraftStatus.unpublished) {
          const server = getSessionManagementServiceInstance();
          if ((result as any).ownerId !== server.sessionUserId()) {
            setHideEditingLink(true);
          }
        }
      });
    }
  },
  // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [props.content.draftId]
  );

  const handleEditFlow = () => {
    if (flowInfo) {
      getAppStore().dispatch(editFlow(flowInfo));
    }
  };

  const projectLink = getProjectLink(flowInfo.project, urls);
  const personalSpaceLink = getPersonalSpaceLink(urls);
  const locationType = flowInfo.location?.type;
  const ownerId = content.owner?.id;

  return (
    <Tooltip>
      <Name name={content.name} />
      <MarkupField markup={props.content.description}
                  testId={TooltipDescriptionTestId}
                  extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={props.content.tags} />
      <BasicField name={Messages.inputSteps()} value={flowInfo.inputCount} />
      <BasicField name={Messages.outputSteps()} value={flowInfo.outputCount} />
      {!FeatureFlags.personalSpaceEnabled && !props.inProjectSpace && <RelatedEntity label={Messages.project()} link={projectLink} /> }
      {FeatureFlags.personalSpaceEnabled && <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace} locationType={locationType}
        projectLink={projectLink} personalSpaceLink={personalSpaceLink} contentOwnerId={ownerId}/>}
      <User user={props.content.owner} label={Messages.owner()} urls={urls} />
      <Timestamp label={Messages.modified()} value={props.content.updatedAt} />
      {flowInfo.draftStatus !== ContentDraftStatus.none &&
        <TooltipDraftIndicator
          indicatorText={() => draftLastEdited}
          linkText={Messages.continueEditing}
          handleAction={handleEditFlow}
          hideEditingLink={hideEditingLink}
        />}
    </Tooltip>
  );
}
